import { Injectable, inject } from '@angular/core';
import { Observable, tap, catchError, finalize, take, delay } from 'rxjs';
import { StudentAssignmentApiService } from './student-assignment-api.service';
import { StudentAssignmentStateService } from './student-assignment-state.service';
import { StudentSubmissionService } from './student-submission.service';
import {
  SubmissionResult,
  StudentAssignmentStep,
  StudentAssignmentErrorType,
  GRADING_PROGRESS_STEPS,
} from './student-assignment.models';

@Injectable({
  providedIn: 'root',
})
export class StudentGradingService {
  private readonly apiService = inject(StudentAssignmentApiService);
  private readonly state = inject(StudentAssignmentStateService);
  private readonly submissionService = inject(StudentSubmissionService);

  private progressInterval: ReturnType<typeof setInterval> | null = null;

  // ============================================
  // SUBMISSION & GRADING
  // ============================================

  submitAndGrade(): Observable<SubmissionResult> {
    const shareCode = this.state.shareCode;
    if (!shareCode) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No share code'));
      });
    }

    this.state.setIsSubmitting(true);
    this.state.setIsGrading(true);
    this.state.setGradingProgressStep(0);
    this.state.setError(null);

    // Transition to result step immediately to show grading progress
    this.state.setStep(StudentAssignmentStep.RESULT);

    // Start animated progress steps
    this.startProgressAnimation();

    return this.apiService.submit(shareCode).pipe(
      tap((result) => {
        this.stopProgressAnimation();
        this.state.setResult(result);
        // Show the final progress step before revealing the result
        this.state.setGradingProgressStep(GRADING_PROGRESS_STEPS.length - 1);
      }),
      delay(800),
      tap(() => {
        this.state.setIsSubmitting(false);
        this.state.setIsGrading(false);
      }),
      catchError((err) => {
        this.stopProgressAnimation();
        this.submissionService.handleSubmissionError(err);
        throw err;
      }),
      finalize(() => {
        this.stopProgressAnimation();
        this.state.setIsSubmitting(false);
        this.state.setIsGrading(false);
      }),
    );
  }

  // ============================================
  // LOAD RESULT (FOR ALREADY GRADED)
  // ============================================

  loadResult(): Observable<SubmissionResult> {
    const shareCode = this.state.shareCode;
    if (!shareCode) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No share code'));
      });
    }

    this.state.setIsLoading(true);

    return this.apiService.getResult(shareCode).pipe(
      tap((result) => {
        this.state.setResult(result);
        this.state.setStep(StudentAssignmentStep.RESULT);
      }),
      catchError((err) => {
        this.state.setError('Failed to load result.');
        throw err;
      }),
      finalize(() => this.state.setIsLoading(false)),
    );
  }

  // ============================================
  // TIME EXPIRED
  // ============================================

  handleTimeExpired(): void {
    // Auto-submit when time runs out
    this.submitAndGrade()
      .pipe(take(1))
      .subscribe({
        error: () => {
          // If submit fails, just show error
          this.state.setErrorType(StudentAssignmentErrorType.EXPIRED);
          this.state.setError(
            'Time limit exceeded. Your submission has expired.',
          );
          this.state.setStep(StudentAssignmentStep.ERROR);
        },
      });
  }

  // ============================================
  // PROGRESS ANIMATION
  // ============================================

  private startProgressAnimation(): void {
    let step = 0;
    this.progressInterval = setInterval(() => {
      step++;
      if (step < GRADING_PROGRESS_STEPS.length) {
        this.state.setGradingProgressStep(step);
      } else {
        this.stopProgressAnimation();
      }
    }, 1500);
  }

  private stopProgressAnimation(): void {
    if (this.progressInterval !== null) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }
}
